import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { updatePersonalizationSettingsAction as updatePersonalizationSettingsOperation } from 'editor/src/store/design/slice';
import getLayoutSchemas from 'editor/src/store/editor/selector/getLayoutSchemas';
import isChangeBackgroundAllowed from 'editor/src/store/editor/selector/isChangeBackgroundAllowed';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import Checkbox from 'editor/src/component/Checkbox';
import CollapsableContainer from 'editor/src/component/CollapsableContainer';

import styles from './index.module.scss';

function CustomerSettings() {
  const { t } = useTranslation();
  const allowSpreadBackgroundColorChange = useSelector(isChangeBackgroundAllowed);
  // Product-level check: show if the design has any layout schemas, regardless of which
  // spread is currently active. getHasAvailableLayouts is spread-specific and would hide
  // the checkbox whenever the merchant is viewing a spread without layouts.
  const showLayoutCheckbox = useSelector((state) => getLayoutSchemas(state).length > 0);

  const addOn = useSelector((state) => !!state.design.designData?.personalizationSettings?.allowAddElements);
  const removeOn = useSelector((state) => !!state.design.designData?.personalizationSettings?.allowRemoveElements);
  const changeBgOn = useSelector(
    (state) => !!state.design.designData?.personalizationSettings?.allowChangeBackgroundColor,
  );
  const chooseLayoutsOn = useSelector(
    (state) => !!state.design.designData?.personalizationSettings?.allowChooseLayouts,
  );
  const dispatch = useDispatch();

  const onAddChange = useCallback((on: boolean) => {
    dispatch(updatePersonalizationSettingsOperation({ allowAddElements: on || undefined }));
  }, []);

  const onRemoveChange = useCallback((on: boolean) => {
    dispatch(updatePersonalizationSettingsOperation({ allowRemoveElements: on || undefined }));
  }, []);

  const onChangeBgChange = useCallback((on: boolean) => {
    dispatch(updatePersonalizationSettingsOperation({ allowChangeBackgroundColor: on || undefined }));
  }, []);

  const onChooseLayoutsChange = useCallback((on: boolean) => {
    dispatch(updatePersonalizationSettingsOperation({ allowChooseLayouts: on || undefined }));
  }, []);

  return (
    <CollapsableContainer title={t('Customer Settings')}>
      <div className={styles.CustomerSettings}>
        <div className="mb-1">{t('Allow customers to')}:</div>
        <Checkbox on={addOn} onChange={onAddChange} label={t('Add layers')} className="cy-add-layers-checkbox" />
        <Checkbox
          on={removeOn}
          onChange={onRemoveChange}
          label={t('Remove layers')}
          className="cy-remove-layers-checkbox"
        />
        {allowSpreadBackgroundColorChange && (
          <Checkbox
            on={changeBgOn}
            onChange={onChangeBgChange}
            label={t('Change background color')}
            className="cy-change-bg-checkbox"
          />
        )}
        {showLayoutCheckbox && (
          <Checkbox
            on={chooseLayoutsOn}
            onChange={onChooseLayoutsChange}
            label={t('Choose layouts')}
            className="cy-choose-layouts-checkbox"
          />
        )}
      </div>
    </CollapsableContainer>
  );
}

export default CustomerSettings;
